import {useDispatch} from 'react-redux';
import {useApiMutation} from 'src/hooks/useApiMutation';
import * as reduxApi from 'src/utils/redux-api-v2';
import type {DashboardDetail} from 'src/types/byod';
import {useDashboardStore} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {
  buildSaveAndPublishPayload,
  type SaveAndPublishRequest,
} from 'src/components/analytics-genesis/byod/store/buildSaveAndPublishPayload';

import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {useByodMutationErrorHandler} from 'src/components/analytics-genesis/byod/api/error-handling';

/**
 * Save & Publish response is just the canonical dashboard payload —
 * same shape as `GET /dashboards/{id}`. No id-swap map; placement ids
 * are frontend-minted UUIDs that round-trip unchanged.
 */
export type SaveAndPublishResponse = DashboardDetail;

/**
 * Atomic save-and-publish. Routes to the create endpoint if the store has
 * no dashboard id (brand-new dashboard), otherwise to the edit endpoint.
 *
 * Reads store state at call time via `getState()` so callers never have to
 * build the payload themselves — `mutateAsync()` with no args is enough.
 *
 * IMPORTANT: store mutations + cache invalidations are NOT done here. They
 * live in the calling site (`runSaveAndPublish` in dashboard-builder/index.tsx)
 * because React Query mutation lifecycle callbacks fire regardless of mount
 * state. If the user navigates to a different dashboard while the POST is
 * in flight, an `onSuccess` here would write the response into the *new*
 * dashboard's store — corrupting it. The caller checks "are we still on
 * the dashboard we tried to save" before applying the response.
 *
 * Toast suppressed (`silent: true`) — the inline error Banner is the
 * primary surface for this failure; a toast on top is duplicate noise.
 */
export function useSaveAndPublish() {
  const dispatch = useDispatch();
  const onError = useByodMutationErrorHandler('save and publish dashboard', {
    silent: true,
  });

  return useApiMutation<SaveAndPublishResponse, Error, void>({
    mutationFn: () => {
      const state = useDashboardStore.getState();
      const payload: SaveAndPublishRequest = buildSaveAndPublishPayload({
        name: state.name,
        description: state.description,
        rowOrder: state.rowOrder,
        rows: state.rows,
        widgets: state.widgets,
      });

      const url =
        state.id === null
          ? `${BASE}/dashboards/save-and-publish`
          : `${BASE}/dashboards/${encodeURIComponent(
              state.id,
            )}/save-and-publish`;

      return dispatch(
        reduxApi.post(url, payload),
      ) as Promise<SaveAndPublishResponse>;
    },
    onError,
  });
}
